import { Building2, ChevronDown, KeyRound, Megaphone, Store, Sun } from "lucide-react";
import { useMall } from "@/context/MallContext";
import PlazaMap from "@/components/mall/map/PlazaMap";

/** The five kinds of sign that hang on this floor. Every homepage block wears exactly one. */
export const TAGS = {
    mall: { label: "13thav billboard", icon: Megaphone },
    store: { label: "Shop billboard", icon: Store },
    plaza: { label: "Community", icon: Sun },
    wide: { label: "Every shop", icon: Building2 },
    office: { label: "Office", icon: KeyRound },
};

/** Whose sign is this? A mall billboard belongs to the building, never to one shop. */
export function BoardTag({ room, kind = "mall", note = "", dark = false, className = "" }) {
    const { label, icon: Icon } = TAGS[kind] || TAGS.mall;
    return (
        <span data-testid={`board-tag-${room}`}
            className={`inline-flex w-fit items-center gap-2 rounded-full border px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] ${className}`}
            style={dark
                ? { borderColor: "rgba(255,255,255,0.22)", background: "rgba(0,0,0,0.4)", color: "#d4d4d8" }
                : { borderColor: "#d4d4d8", background: "#fafafa", color: "#3f3f46" }}>
            <Icon size={11} /> {label}{note ? ` · ${note}` : ""}
        </span>
    );
}

/** The menu name for each room, so the plate on the sign says exactly what's behind it. */
export const ROOM_NAMES = {
    white: "Shirts · Same Day",
    dtf: "Shirts · DTF Gang Sheets",
    apparel: "Standard · Apparel",
    signs: "Signs",
    artwork: "Artwork",
    classifieds: "Classifieds",
    classes: "Classes",
    website: "Websites",
    wholesale: "Wholesale",
};

/**
 * The mall billboard itself — a thin metal frame on the atrium wall with its name plate hung on
 * the TOP rail, half on the sign and half above it, so you know what's coming as you scroll.
 */
export function BillboardFrame({ children, testid, room, kind = "mall", note = "", className = "" }) {
    const { solo, store } = useMall();
    const { label, icon: Icon } = TAGS[(solo || store) ? "store" : kind] || TAGS.mall;
    const title = ROOM_NAMES[room] || note || label;
    return (
        <div className={`relative bg-zinc-100 px-3 pb-6 pt-14 sm:px-6 sm:pb-8 sm:pt-16 ${className}`} data-testid={testid}>
            <div className="pointer-events-none absolute inset-0"
                style={{ background: "radial-gradient(120% 70% at 50% 0%, #ffffff 0%, #f4f4f5 45%, #e9e9ec 100%)" }} />
            <div className="pointer-events-none absolute inset-0 opacity-[0.35]"
                style={{ backgroundImage: "linear-gradient(#d4d4d8 1px, transparent 1px)", backgroundSize: "100% 56px" }} />

            <div className="relative mx-auto max-w-[92rem]">
                <div className="relative rounded-[26px] border-2 border-zinc-300 bg-white p-1.5 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.35),inset_0_1px_0_#ffffff]">
                    <div className="overflow-hidden rounded-[19px] border border-zinc-200">{children}</div>

                    <span data-testid={`board-tag-${room}`}
                        className="absolute left-1/2 top-0 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center whitespace-nowrap rounded-2xl border-2 border-zinc-300 bg-white px-5 py-2 text-center shadow-[0_16px_34px_-14px_rgba(0,0,0,0.5)] sm:px-10 sm:py-2.5">
                        <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.26em] text-zinc-400">
                            <Icon size={11} /> {label}
                        </span>
                        <span className="font-display text-base font-extrabold uppercase leading-tight tracking-tight text-zinc-900 sm:text-2xl">
                            {title}
                        </span>
                        {note && (
                            <span className="hidden font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400 sm:block">
                                {note}
                            </span>
                        )}
                    </span>
                </div>

                <div className="relative mx-auto h-8 w-full">
                    <span className="absolute left-1/2 top-0 h-8 w-3 -translate-x-[9rem] rounded-b-md bg-gradient-to-b from-zinc-300 to-zinc-200" />
                    <span className="absolute left-1/2 top-0 h-8 w-3 translate-x-[8rem] rounded-b-md bg-gradient-to-b from-zinc-300 to-zinc-200" />
                </div>
            </div>
        </div>
    );
}

/**
 * The detail behind the sign. Every word from the board stays — it just folds away so the
 * section fits on one screen and the headline gets room to breathe.
 */
export function MoreDetails({ label, children, dark = false, testid, className = "" }) {
    return (
        <details className={`group relative ${className}`} data-testid={testid}>
            <summary className={`flex cursor-pointer list-none items-center justify-between gap-4 border-t py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors [&::-webkit-details-marker]:hidden ${dark ? "border-white/15 text-zinc-400 hover:text-white" : "border-zinc-200 text-zinc-500 hover:text-zinc-900"}`}>
                {label}
                <ChevronDown size={14} className="shrink-0 transition-transform duration-300 group-open:rotate-180" />
            </summary>
            <div className="pt-3">{children}</div>
        </details>
    );
}

/** The open court. No frame, no roof — a shared space anybody can stand in. */export function PlazaShell({ children, testid, accent = "#3F3F46", className = "" }) {
    return (
        <section className={`relative overflow-hidden border-y border-zinc-200 bg-white ${className}`} data-testid={testid}>
            <div className="pointer-events-none absolute inset-0"
                style={{ background: "radial-gradient(90% 120% at 50% -20%, #ffffff 0%, #fbfbfc 40%, #f2f2f4 100%)" }} />
            <div className="pointer-events-none absolute inset-0 opacity-[0.5]"
                style={{
                    backgroundImage: "radial-gradient(#d4d4d8 1.1px, transparent 1.1px), radial-gradient(#e4e4e7 1.1px, transparent 1.1px)",
                    backgroundSize: "26px 26px, 26px 26px", backgroundPosition: "0 0, 13px 13px",
                }} />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24"
                style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.95), transparent)" }} />
            <PlazaMap accent={accent}
                className="pointer-events-none absolute -right-8 top-8 hidden w-72 opacity-[0.22] lg:block" />
            <div className="relative">{children}</div>
        </section>
    );
}

/** The hallway is numbered — you always know which room you're standing outside of. */
export function RoomNumber({ n, room, accent, dark = false, className = "" }) {
    return (
        <p className={`flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.32em] ${className}`}
            style={{ color: dark ? "#71717a" : "#a1a1aa" }} data-testid={`room-number-${n}`}>
            <span style={{ color: accent }}>{n}</span>
            <span className="h-px w-10" style={{ background: dark ? "rgba(255,255,255,0.18)" : "#e4e4e7" }} />
            {room}
        </p>
    );
}

/** A list of things you can actually buy, marked in the room's accent. */
export function Bullets({ items, accent = "#0B0B0B", dark = false, className = "", testid }) {
    return (
        <ul className={`space-y-2 ${className}`} data-testid={testid}>
            {items.map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm" style={{ color: dark ? "#d4d4d8" : "#3f3f46" }}>
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45" style={{ background: accent }} />
                    {t}
                </li>
            ))}
        </ul>
    );
}

/** The price board: label on the left, the number on the right, monospaced. */
export function Lines({ rows, accent = "#0B0B0B", dark = false, className = "", testid }) {
    return (
        <dl className={`divide-y ${className}`} data-testid={testid}
            style={{ borderColor: dark ? "rgba(255,255,255,0.1)" : "#f4f4f5" }}>
            {rows.map(([label, value]) => (
                <div key={label + value} className="flex items-baseline justify-between gap-4 py-2.5">
                    <dt className="text-sm" style={{ color: dark ? "#a1a1aa" : "#52525b" }}>{label}</dt>
                    <dd className="text-right font-mono text-[11px] uppercase tracking-[0.1em]" style={{ color: accent }}>{value}</dd>
                </div>
            ))}
        </dl>
    );
}

/** A framed card inside a billboard — the spec sheet under the headline. */
export function Panel({ icon: Icon, title, accent = "#0B0B0B", dark = false, children, className = "", testid }) {
    return (
        <div className={`rounded-3xl border p-6 ${className}`} data-testid={testid}
            style={dark
                ? { borderColor: "rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)" }
                : { borderColor: "#e4e4e7", background: "#ffffff" }}>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: accent }}>
                {Icon && <Icon size={13} />} {title}
            </p>
            <div className="mt-4">{children}</div>
        </div>
    );
}

/** Small print that still matters — policies, pickup, the fine line at the bottom. */
export function FinePrint({ items, dark = false, className = "", testid }) {
    return (
        <div className={`grid gap-5 md:grid-cols-2 lg:grid-cols-4 ${className}`} data-testid={testid}>
            {items.map(([title, copy]) => (
                <div key={title} className="border-t pt-4" style={{ borderColor: dark ? "rgba(255,255,255,0.14)" : "#e4e4e7" }}>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: dark ? "#a1a1aa" : "#71717a" }}>{title}</p>
                    <p className="mt-2 text-xs leading-relaxed" style={{ color: dark ? "#d4d4d8" : "#52525b" }}>{copy}</p>
                </div>
            ))}
        </div>
    );
}
